import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, 
  UserPlus, 
  Building2, 
  Stethoscope, 
  Search,
  Loader2,
  MapPin,
  Phone,
  Mail,
  TrendingUp,
  Award,
  Wallet,
  ChevronRight,
  Calendar,
  Eye,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const facilityTypeLabels = {
  hospital: 'Hôpital',
  clinic: 'Clinique',
  pharmacy: 'Pharmacie',
  laboratory: 'Laboratoire',
  imaging_center: "Centre d'imagerie",
  maternity: 'Maternité',
  dental_clinic: 'Cabinet dentaire',
};

const AgentTracking = () => {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [recruits, setRecruits] = useState([]);
  const [loadingRecruits, setLoadingRecruits] = useState(false);
  const [recruitsFilter, setRecruitsFilter] = useState('all');

  useEffect(() => {
    loadAgentsData();
  }, []);

  const loadAgentsData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/agents/tracking');
      setAgents(response.data.agents || []);
      setGlobalStats(response.data.global_stats || {});
    } catch (error) {
      console.error('Error loading agents data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadAgentRecruits = async (agentId, role = null) => {
    try {
      setLoadingRecruits(true);
      const params = role && role !== 'all' ? `?role=${role}` : '';
      const response = await api.get(`/admin/agents/${agentId}/recruits${params}`);
      setRecruits(response.data.recruits || []);
    } catch (error) {
      console.error('Error loading recruits:', error);
      toast.error('Erreur lors du chargement des recrutés');
    } finally {
      setLoadingRecruits(false);
    }
  };

  const openAgentDetail = async (agent) => {
    setSelectedAgent(agent);
    setRecruitsFilter('all');
    setShowDetailModal(true);
    await loadAgentRecruits(agent.id);
  };

  const handleRecruitsFilterChange = async (filter) => {
    setRecruitsFilter(filter);
    if (selectedAgent) {
      await loadAgentRecruits(selectedAgent.id, filter === 'all' ? null : filter);
    }
  };

  const filteredAgents = agents.filter(agent => 
    agent.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agent-tracking">
      {/* Global Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-orange-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-orange-700">Agents</p>
                <p className="text-xl font-bold text-orange-900">{globalStats?.total_agents || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-700">Total Recrutés</p>
                <p className="text-xl font-bold text-blue-900">{globalStats?.total_recruits || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-emerald-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-emerald-700">Patients</p>
                <p className="text-xl font-bold text-emerald-900">{globalStats?.total_patients_recruited || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-red-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
                <Stethoscope className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-red-700">Professionnels</p>
                <p className="text-xl font-bold text-red-900">{globalStats?.total_professionals_recruited || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-indigo-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-indigo-700">Établissements</p>
                <p className="text-xl font-bold text-indigo-900">{globalStats?.total_facilities_recruited || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                <Wallet className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-amber-700">Commissions</p>
                <p className="text-lg font-bold text-amber-900">{formatCurrency(globalStats?.total_commissions)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-purple-700">Moy./Agent</p>
                <p className="text-xl font-bold text-purple-900">{globalStats?.avg_recruits_per_agent || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Refresh */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher un agent (nom, email, ville)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="agent-search-input"
          />
        </div>
        <Button variant="outline" onClick={loadAgentsData} className="shrink-0">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Agents List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Award className="w-5 h-5 text-orange-600" />
            Agents Terrain ({filteredAgents.length})
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Suivi des performances de recrutement par agent
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {filteredAgents.length > 0 ? (
            <div className="space-y-3">
              {filteredAgents.map((agent, index) => (
                <div
                  key={agent.id}
                  className="p-3 sm:p-4 rounded-xl bg-slate-50 border hover:border-orange-200 transition-colors"
                  data-testid={`agent-card-${agent.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-sm sm:text-base">
                          {agent.full_name?.charAt(0) || '?'}
                        </div>
                        {index < 3 && (
                          <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                            index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : 'bg-amber-700'
                          }`}>
                            {index + 1}
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 text-sm sm:text-base">{agent.full_name || 'Agent'}</h3>
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-0.5">
                          {agent.city && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {agent.city}
                            </span>
                          )}
                          <Badge className={`text-xs ${agent.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {agent.is_active !== false ? 'Actif' : 'Inactif'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openAgentDetail(agent)}
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      data-testid={`view-agent-${agent.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Détails</span>
                    </Button>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-lg sm:text-xl font-bold text-blue-600">{agent.stats?.total_recruits || 0}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-lg sm:text-xl font-bold text-emerald-600">{agent.stats?.patients_count || 0}</p>
                      <p className="text-xs text-slate-500">Patients</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-lg sm:text-xl font-bold text-red-600">{agent.stats?.professionals_count || 0}</p>
                      <p className="text-xs text-slate-500">Pros</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-lg sm:text-xl font-bold text-indigo-600">{agent.stats?.facilities_count || 0}</p>
                      <p className="text-xs text-slate-500">Établ.</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center col-span-2 sm:col-span-1">
                      <p className="text-sm sm:text-base font-bold text-amber-600">{formatCurrency(agent.stats?.total_earned)}</p>
                      <p className="text-xs text-slate-500">Gagné</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center hidden sm:block">
                      <p className="text-sm font-bold text-purple-600">{formatCurrency(agent.stats?.pending_commission)}</p>
                      <p className="text-xs text-slate-500">En attente</p>
                    </div>
                  </div>

                  {/* Types breakdown (desktop) */}
                  {(Object.keys(agent.stats?.professional_types || {}).length > 0 || Object.keys(agent.stats?.facility_types || {}).length > 0) && (
                    <div className="hidden sm:flex flex-wrap gap-1 mt-3 pt-3 border-t border-slate-200">
                      {Object.entries(agent.stats?.professional_types || {}).map(([type, count]) => (
                        <Badge key={type} variant="outline" className="text-xs bg-white">
                          {type}: {count}
                        </Badge>
                      ))}
                      {Object.entries(agent.stats?.facility_types || {}).map(([type, count]) => (
                        <Badge key={type} variant="outline" className="text-xs bg-white text-indigo-700 border-indigo-200">
                          {facilityTypeLabels[type] || type}: {count}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {searchTerm ? 'Aucun agent trouvé pour cette recherche' : 'Aucun agent terrain enregistré'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agent Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold">
                {selectedAgent?.full_name?.charAt(0) || '?'}
              </div>
              <div>
                <span className="text-base sm:text-lg">{selectedAgent?.full_name || 'Agent'}</span>
                <p className="text-xs text-slate-500 font-normal">{selectedAgent?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedAgent && (
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
              {/* Contact Info */}
              <div className="flex flex-wrap gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
                {selectedAgent.phone && (
                  <span className="text-sm text-slate-600 flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {selectedAgent.phone}
                  </span>
                )}
                {selectedAgent.city && (
                  <span className="text-sm text-slate-600 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {selectedAgent.city}
                  </span>
                )}
                <span className="text-sm text-slate-600 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Inscrit le {formatDate(selectedAgent.created_at)}
                </span>
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Card className="border-0 shadow-sm bg-emerald-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{selectedAgent.stats?.patients_count || 0}</p>
                    <p className="text-xs text-emerald-600">Patients</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-red-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{selectedAgent.stats?.professionals_count || 0}</p>
                    <p className="text-xs text-red-600">Professionnels</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-indigo-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-700">{selectedAgent.stats?.facilities_count || 0}</p>
                    <p className="text-xs text-indigo-600">Établissements</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-amber-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-lg font-bold text-amber-700">{formatCurrency(selectedAgent.stats?.total_earned)}</p>
                    <p className="text-xs text-amber-600">Commissions</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recruits List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Recrutés</h3>
                  <div className="flex gap-1">
                    {['all', 'patient', 'professional', 'facility'].map(filter => (
                      <Button
                        key={filter}
                        size="sm"
                        variant={recruitsFilter === filter ? 'default' : 'ghost'}
                        onClick={() => handleRecruitsFilterChange(filter)}
                        className={`text-xs h-7 ${recruitsFilter === filter ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                      >
                        {filter === 'all' ? 'Tous' : filter === 'patient' ? 'Patients' : filter === 'professional' ? 'Pros' : 'Établ.'}
                      </Button>
                    ))}
                  </div>
                </div>

                {loadingRecruits ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                  </div>
                ) : recruits.length > 0 ? (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2 pr-4">
                      {recruits.map(recruit => (
                        <div
                          key={recruit.id}
                          className="flex items-center gap-3 p-3 bg-white rounded-lg border"
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                            recruit.role === 'patient' ? 'bg-emerald-100' :
                            recruit.role === 'professional' ? 'bg-red-100' : 'bg-indigo-100'
                          }`}>
                            {recruit.role === 'patient' ? (
                              <Users className="w-4 h-4 text-emerald-600" />
                            ) : recruit.role === 'professional' ? (
                              <Stethoscope className="w-4 h-4 text-red-600" />
                            ) : (
                              <Building2 className="w-4 h-4 text-indigo-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900 truncate">
                              {recruit.full_name || recruit.facility_name || '-'}
                            </p>
                            <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                              {recruit.role === 'professional' && recruit.specialty && (
                                <Badge variant="outline" className="text-xs">{recruit.specialty}</Badge>
                              )}
                              {recruit.role === 'facility' && recruit.facility_type && (
                                <Badge variant="outline" className="text-xs text-indigo-600">
                                  {facilityTypeLabels[recruit.facility_type] || recruit.facility_type}
                                </Badge>
                              )}
                              {recruit.city && (
                                <span className="flex items-center gap-0.5">
                                  <MapPin className="w-3 h-3" />
                                  {recruit.city}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-slate-400 shrink-0">
                            {formatDate(recruit.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Aucun recruté trouvé</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentTracking;
